import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { Gift, Plus, Save, Trash2, X, Edit2, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';

interface GiftCatalogItem {
  id: number;
  key: string;
  name: string;
  description: string;
  isActive: number;
  createdAt?: number;
}

export default function AdminGiftCatalog() {
  const navigate = useNavigate();
  const [items, setItems] = useState<GiftCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<GiftCatalogItem | null>(null);
  const [form, setForm] = useState<Partial<GiftCatalogItem>>({ key: '', name: '', description: '', isActive: 1 });

  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/public/admin/gift-catalog');
      if (!res.ok) throw new Error('載入失敗');
      const data = await res.json();
      setItems(Array.isArray(data) ? data : data.data || []);
    } catch (e: any) {
      setError(e.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    fetchItems();
  }, [navigate]);

  const showToast = (msg: string, isError = false) => {
    if (isError) { setError(msg); setTimeout(() => setError(null), 4000); }
    else { setSuccess(msg); setTimeout(() => setSuccess(null), 3000); }
  };

  const openCreate = () => {
    setEditingItem(null);
    setForm({ key: '', name: '', description: '', isActive: 1 });
    setModalOpen(true);
  };

  const openEdit = (item: GiftCatalogItem) => {
    setEditingItem(item);
    setForm({ ...item });
    setModalOpen(true);
  };

  const saveItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.key?.trim() || !form.name?.trim()) {
      showToast('請填寫 Key 與名稱', true); return;
    }
    if (!/^[a-z0-9_-]+$/.test(form.key.trim())) {
      showToast('Key 只可使用小寫英文、數字、底線或橫線', true); return;
    }
    try {
      const url = editingItem
        ? `/api/public/admin/gift-catalog/${editingItem.id}`
        : '/api/public/admin/gift-catalog';
      const res = await apiFetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '儲存失敗');
      showToast(editingItem ? '已更新禮品' : '已建立禮品');
      setModalOpen(false);
      fetchItems();
    } catch (err: any) { showToast(err.message || '儲存失敗', true); }
  };

  const deleteItem = async (item: GiftCatalogItem) => {
    if (!confirm(`確定刪除「${item.name}」？`)) return;
    try {
      const res = await apiFetch(`/api/public/admin/gift-catalog/${item.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('刪除失敗');
      showToast('已刪除'); fetchItems();
    } catch (err: any) { showToast(err.message || '刪除失敗', true); }
  };

  const toggleActive = async (item: GiftCatalogItem) => {
    try {
      const res = await apiFetch(`/api/public/admin/gift-catalog/${item.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !item.isActive }),
      });
      if (!res.ok) throw new Error('更新失敗');
      fetchItems();
    } catch (err: any) { showToast(err.message || '更新失敗', true); }
  };

  return (
    <AdminLayout currentPage="gift-catalog">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <Gift className="w-6 h-6 text-red-600" />
          禮品目錄
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          管理可透過 Workflow 贈送的隨單禮品。禮品會掛在客戶名下，並在下一次落單時自動附送。
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex justify-between items-center dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <span className="flex items-center gap-2"><AlertCircle size={16} /> {error}</span>
          <button onClick={() => setError(null)}><X size={18} /></button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex justify-between items-center dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
          <span className="flex items-center gap-2"><CheckCircle size={16} /> {success}</span>
          <button onClick={() => setSuccess(null)}><X size={18} /></button>
        </div>
      )}

      <div className="mb-4 flex justify-end gap-2">
        <button onClick={fetchItems} className="flex items-center gap-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white">
          <RefreshCw size={16} /> 重新整理
        </button>
        <button onClick={openCreate} className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors">
          <Plus size={16} /> 新增禮品
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">載入中...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          尚未建立禮品
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-300">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Key</th>
                <th className="px-4 py-3 text-left font-medium">名稱</th>
                <th className="px-4 py-3 text-left font-medium">說明</th>
                <th className="px-4 py-3 text-left font-medium">狀態</th>
                <th className="px-4 py-3 text-right font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {items.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3 font-mono text-xs text-gray-500 dark:text-gray-400">{item.key}</td>
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{item.name}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300 max-w-md truncate">{item.description}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleActive(item)} className={`text-xs px-2 py-1 rounded ${item.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {item.isActive ? '啟用' : '停用'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openEdit(item)} className="text-blue-600 hover:text-blue-700" title="編輯"><Edit2 size={16} /></button>
                      <button onClick={() => deleteItem(item)} className="text-red-600 hover:text-red-700" title="刪除"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-xl w-full">
            <form onSubmit={saveItem} className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{editingItem ? '編輯禮品' : '新增禮品'}</h2>
                <button type="button" onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={22} /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Key *</label>
                  <input disabled={!!editingItem} value={form.key || ''} onChange={(e) => setForm({ ...form, key: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white disabled:bg-gray-100" />
                  <p className="text-xs text-gray-400 mt-1">Workflow 會用此 Key 引用禮品，建立後不可修改。</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">名稱 *</label>
                  <input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">說明</label>
                  <textarea value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none bg-white dark:bg-gray-800 dark:text-white" />
                </div>
                <div className="flex items-center gap-2">
                  <input id="gift-active" type="checkbox" checked={!!form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked ? 1 : 0 })} className="w-4 h-4 text-red-600 rounded" />
                  <label htmlFor="gift-active" className="text-sm text-gray-700 dark:text-gray-300">啟用</label>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white">取消</button>
                <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 flex items-center gap-2"><Save size={16} /> 儲存</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
